/**
 * AdminMessageBroadcastModal — sends a 1-to-many message via the
 * `/api/messaging/broadcasts` endpoint to a target audience (Users only,
 * Writers only, or Both). Surfaces on the Admin Dashboard under the
 * "Send to all" button. Spec Feb 2026 (user choice 2c: filterable by role).
 *
 * Difference vs AdminAnnouncementComposer:
 *   - This system delivers the message into each recipient's Inbox
 *     (the messaging bell) — they keep a copy and can reply.
 *   - The announcement composer posts a TRANSIENT banner that everyone
 *     can dismiss; no inbox copy.
 */
import React, { useState } from 'react';
import { X, Send, Loader2, Megaphone, Users as UsersIcon, PenTool, Globe2, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AUDIENCES = [
  { id: 'all',     label: 'Everyone',      icon: Globe2,    hint: 'Users + Writers + Admins' },
  { id: 'users',   label: 'Users only',    icon: UsersIcon, hint: 'Readers' },
  { id: 'writers', label: 'Writers only',  icon: PenTool,   hint: 'Authors + creators' },
  { id: 'admins',  label: 'Admins only',   icon: Shield,    hint: 'Admins + co-admins' },
];

const AdminMessageBroadcastModal = ({ open, onClose }) => {
  const [audience, setAudience] = useState('all');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault();
    const text = body.trim();
    if (!text) {
      toast.error('Write something to send');
      return;
    }
    setSending(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/messaging/broadcasts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ audience, body: text }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || `Failed (HTTP ${r.status})`);
      toast.success(`Broadcast sent to ${audience === 'all' ? 'everyone' : audience}`);
      setBody('');
      onClose?.();
    } catch (err) {
      toast.error(err.message || 'Could not send broadcast');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="msg-broadcast-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-slate-900 border border-indigo-500/40 rounded-2xl shadow-2xl"
        data-testid="msg-broadcast-modal"
      >
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <h2 className="text-base font-bold text-white flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-indigo-300" />
            Send message to all
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full hover:bg-slate-800 flex items-center justify-center text-gray-400 hover:text-white"
            data-testid="msg-broadcast-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="p-4 space-y-4">
          <div>
            <p className="text-[11px] uppercase tracking-widest font-bold text-slate-400 mb-2">Audience</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {AUDIENCES.map((a) => {
                const Icon = a.icon;
                const active = audience === a.id;
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => setAudience(a.id)}
                    className={`text-left rounded-lg border p-2.5 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300 ${
                      active
                        ? 'bg-indigo-500/20 border-indigo-400 text-white'
                        : 'bg-slate-800/60 border-slate-700 text-slate-300 hover:border-slate-500'
                    }`}
                    data-testid={`msg-broadcast-aud-${a.id}`}
                  >
                    <Icon className={`w-4 h-4 mb-1 ${active ? 'text-indigo-200' : 'text-slate-400'}`} />
                    <p className="text-xs font-bold leading-tight">{a.label}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{a.hint}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <p className="text-[11px] uppercase tracking-widest font-bold text-slate-400 mb-1.5">Message</p>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value.slice(0, 4000))}
              rows={6}
              placeholder="What do you want to tell them?"
              className="w-full rounded-lg bg-slate-800 border border-slate-700 text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 resize-none"
              data-testid="msg-broadcast-body"
            />
            <p className="text-[10px] text-slate-500 mt-1 tabular-nums text-right">{body.length}/4000</p>
          </div>

          <div className="flex items-center justify-between gap-2 pt-1">
            <p className="text-[10px] text-slate-500">
              Delivered to every recipient's inbox. They keep a copy and can reply.
            </p>
            <button
              type="submit"
              disabled={sending || !body.trim()}
              className="px-4 py-2 rounded-lg bg-gradient-to-r from-indigo-500 to-fuchsia-600 hover:from-indigo-400 hover:to-fuchsia-500 text-white font-bold text-sm disabled:opacity-50 flex items-center gap-2"
              data-testid="msg-broadcast-submit"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminMessageBroadcastModal;
